import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../services/api';
import './ImplementationRoadmap.css';

// ============================================================================
// Type Definitions
// ============================================================================

interface PhaseData {
  name: string;
  base_duration: number;
  buffer_days: number;
  total_duration: number;
  start_date: string | null;
  end_date: string | null;
}

interface ApplicationData {
  id: string;
  text: string;
  start_date: string | null;
  end_date: string | null;
  integration_group_id: string;
  integration_group_name: string;
  application_status: string;
  schedule_status: string;
  total_duration_days: number;
  total_effort_days: number;
  phases: PhaseData[];
  deployment_status?: string;
}

interface DeploymentStatusRecord {
  integration_group_id: string;
  application_id: string;
  deployment_status: string;
}

interface IntegrationGroup {
  group_id: string;
  group_name: string;
  app_count?: number;
}

// ============================================================================
// Constants
// ============================================================================

const PHASE_COLORS: Record<string, { primary: string; secondary: string; border: string; bg: string }> = {
  'Assessment & Planning': {
    primary: '#60A5FA',    // Medium blue
    secondary: '#3B82F6',
    border: '#2563EB',
    bg: '#EFF6FF'
  },
  'Design & Architecture': {
    primary: '#C084FC',    // Medium purple
    secondary: '#A855F7',
    border: '#9333EA',
    bg: '#FAF5FF'
  },
  'Migration Execution': {
    primary: '#4ADE80',    // Medium green
    secondary: '#22C55E',
    border: '#16A34A',
    bg: '#F0FDF4'
  },
  'Testing & Validation': {
    primary: '#FBBF24',    // Medium yellow/amber
    secondary: '#F59E0B',
    border: '#D97706',
    bg: '#FFFBEB'
  },
  'Go-Live & Optimization': {
    primary: '#F87171',    // Medium red/coral
    secondary: '#EF4444',
    border: '#DC2626',
    bg: '#FEF2F2'
  }
};

const GRAYSCALE_COLORS = {
  primary: '#9E9E9E',
  secondary: '#757575',
  border: '#616161'
};

const PHASE_ORDER = [
  'Assessment & Planning',
  'Design & Architecture',
  'Migration Execution',
  'Testing & Validation',
  'Go-Live & Optimization'
];

const US_FEDERAL_HOLIDAYS = [
  { month: 1, day: 1, name: "New Year's Day" },
  { month: 1, day: 15, weekday: 1, week: 3, name: "Martin Luther King Jr. Day" },
  { month: 2, day: 15, weekday: 1, week: 3, name: "Presidents' Day" },
  { month: 5, day: 25, weekday: 1, week: -1, name: "Memorial Day" },
  { month: 6, day: 19, name: "Juneteenth" },
  { month: 7, day: 4, name: "Independence Day" },
  { month: 9, day: 1, weekday: 1, week: 1, name: "Labor Day" },
  { month: 10, day: 8, weekday: 1, week: 2, name: "Columbus Day" },
  { month: 11, day: 11, name: "Veterans Day" },
  { month: 11, day: 22, weekday: 4, week: 4, name: "Thanksgiving Day" },
  { month: 12, day: 25, name: "Christmas Day" }
];

// ============================================================================
// Utility Functions
// ============================================================================

const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const getHolidayDate = (holiday: any, year: number): Date | null => {
  if (holiday.weekday !== undefined && holiday.week !== undefined) {
    // Calculate nth weekday of month
    const firstOfMonth = new Date(year, holiday.month - 1, 1);
    let count = 0;
    const targetWeekday = holiday.weekday;

    if (holiday.week === -1) {
      // Last occurrence
      const lastOfMonth = new Date(year, holiday.month, 0);
      for (let d = lastOfMonth.getDate(); d >= 1; d--) {
        const testDate = new Date(year, holiday.month - 1, d);
        if (testDate.getDay() === targetWeekday) {
          return testDate;
        }
      }
    } else {
      // Nth occurrence
      for (let d = 1; d <= 31; d++) {
        const testDate = new Date(year, holiday.month - 1, d);
        if (testDate.getMonth() !== holiday.month - 1) break;
        if (testDate.getDay() === targetWeekday) {
          count++;
          if (count === holiday.week) {
            return testDate;
          }
        }
      }
    }
    return null;
  }
  return new Date(year, holiday.month - 1, holiday.day);
};

const isHoliday = (date: Date): boolean => {
  const year = date.getFullYear();
  for (const holiday of US_FEDERAL_HOLIDAYS) {
    const holidayDate = getHolidayDate(holiday, year);
    if (holidayDate &&
        holidayDate.getFullYear() === date.getFullYear() &&
        holidayDate.getMonth() === date.getMonth() &&
        holidayDate.getDate() === date.getDate()) {
      return true;
    }
  }
  return false;
};

const isBusinessDay = (date: Date): boolean => {
  return !isWeekend(date) && !isHoliday(date);
};

const parseDate = (dateStr: string | null): Date | null => {
  if (!dateStr) return null;
  // Handle ISO format dates with timezone (e.g., "2026-01-05T05:00:00.000Z")
  // and simple date strings (e.g., "2026-01-05")
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    // Try adding time component for simple date strings
    const dateWithTime = new Date(dateStr + 'T00:00:00');
    return isNaN(dateWithTime.getTime()) ? null : dateWithTime;
  }
  return date;
};

const formatDate = (date: Date | null): string => {
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateShort = (date: Date | null): string => {
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

const formatDateMMDDYYYY = (date: Date | null): string => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const daysBetween = (start: Date, end: Date): number => {
  const diffTime = end.getTime() - start.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Calculate business days between two dates (excluding weekends and holidays)
const businessDaysBetween = (start: Date, end: Date): number => {
  let count = 0;
  const current = new Date(start);

  while (current <= end) {
    if (isBusinessDay(current)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
};

// ============================================================================
// Main Component
// ============================================================================

const ImplementationRoadmap: React.FC = () => {
  // State
  const [applications, setApplications] = useState<ApplicationData[]>([]);
  const [deploymentStatuses, setDeploymentStatuses] = useState<DeploymentStatusRecord[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<'gantt' | 'table'>('gantt');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hoveredApp, setHoveredApp] = useState<string | null>(null);
  const [hoveredPhase, setHoveredPhase] = useState<{ appId: string; phase: string } | null>(null);
  const [ganttStartDate, setGanttStartDate] = useState<string>('');

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Constants for Gantt layout
  const SIDEBAR_WIDTH = 280;
  const ROW_HEIGHT = 50;
  const HEADER_HEIGHT = 60;
  const DAY_WIDTH = 20 * zoomLevel;

  // Calculate timeline bounds
  const getTimelineBounds = useCallback(() => {
    const allDates: Date[] = [];
    applications.forEach(app => {
      const startDate = parseDate(app.start_date);
      const endDate = parseDate(app.end_date);
      if (startDate) allDates.push(startDate);
      if (endDate) allDates.push(endDate);
      app.phases.forEach(phase => {
        const phaseStart = parseDate(phase.start_date);
        const phaseEnd = parseDate(phase.end_date);
        if (phaseStart) allDates.push(phaseStart);
        if (phaseEnd) allDates.push(phaseEnd);
      });
    });

    if (allDates.length === 0) {
      const today = new Date();
      return {
        minDate: today,
        maxDate: new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000),
        totalDays: 90
      };
    }

    const validDates = allDates.filter(d => d !== null && !isNaN(d.getTime()));
    if (validDates.length === 0) {
      const today = new Date();
      return {
        minDate: today,
        maxDate: new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000),
        totalDays: 90
      };
    }

    // Use user-selected start date if provided, otherwise auto-detect from data
    let minDate: Date;
    if (ganttStartDate) {
      minDate = new Date(ganttStartDate + 'T00:00:00');
    } else {
      minDate = new Date(Math.min(...validDates.map(d => d.getTime())));
      minDate.setDate(minDate.getDate() - 7);
    }

    const maxDate = new Date(Math.max(...validDates.map(d => d.getTime())));
    maxDate.setDate(maxDate.getDate() + 14);

    // Ensure maxDate is after minDate
    if (maxDate <= minDate) {
      maxDate.setTime(minDate.getTime() + 90 * 24 * 60 * 60 * 1000);
    }

    return {
      minDate,
      maxDate,
      totalDays: daysBetween(minDate, maxDate)
    };
  }, [applications, ganttStartDate]);

  // Fetch integration groups
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await authFetch('/api/integration-groups?exclude_analysis=true');
        const result = await response.json();
        if (result.success) {
          setIntegrationGroups(result.data || []);
        }
      } catch (err) {
        console.error('Error fetching integration groups:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, []);

  // Fetch roadmap data when group changes
  useEffect(() => {
    if (!selectedGroupId) {
      setApplications([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams();
        // If "ALL" is selected, don't filter by group - fetch all
        if (selectedGroupId !== 'ALL') {
          params.append('integration_group_id', selectedGroupId);
        }

        // Fetch roadmap data and deployment statuses in parallel
        const [roadmapResponse, deploymentResponse] = await Promise.all([
          authFetch(`/api/roadmap-schedule/visualization/gantt?${params.toString()}`),
          authFetch('/api/deployment-status')
        ]);

        const result = await roadmapResponse.json();
        let deploymentData: DeploymentStatusRecord[] = [];
        try {
          const depResult = await deploymentResponse.json();
          if (Array.isArray(depResult)) {
            deploymentData = depResult;
            setDeploymentStatuses(depResult);
          }
        } catch (e) {
          // Ignore deployment status errors
        }

        if (result.success) {
          // Merge deployment statuses into applications
          const appsWithStatus = (result.data || []).map((app: ApplicationData) => {
            const depStatus = deploymentData.find(
              ds => ds.integration_group_id === app.integration_group_id && ds.application_id === app.id
            );
            return {
              ...app,
              deployment_status: depStatus?.deployment_status || undefined
            };
          });
          setApplications(appsWithStatus);
        } else {
          setError(result.error || 'Failed to fetch data');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch roadmap data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedGroupId]);

  // Draw Gantt chart on canvas (rows only, header is separate)
  const drawGanttChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || applications.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { minDate, totalDays } = getTimelineBounds();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const canvasWidth = totalDays * DAY_WIDTH;
    // Canvas height is ONLY for rows (no header) - this ensures sync with sidebar
    const canvasHeight = applications.length * ROW_HEIGHT;

    // Set canvas size
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Clear canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw day markers and weekend highlights (starting from y=0 since no header)
    let currentDate = new Date(minDate);

    while (currentDate <= new Date(minDate.getTime() + totalDays * 24 * 60 * 60 * 1000)) {
      const dayOffset = daysBetween(minDate, currentDate);
      const x = dayOffset * DAY_WIDTH;

      // Draw day markers
      if (zoomLevel >= 0.75) {
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasHeight);
        ctx.stroke();
      }

      // Highlight weekends
      if (isWeekend(currentDate)) {
        ctx.fillStyle = 'rgba(229, 231, 235, 0.5)';
        ctx.fillRect(x, 0, DAY_WIDTH, canvasHeight);
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Draw TODAY line
    const todayOffset = daysBetween(minDate, today);
    if (todayOffset >= 0 && todayOffset <= totalDays) {
      const todayX = todayOffset * DAY_WIDTH;

      ctx.strokeStyle = '#EF4444';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(todayX, 0);
      ctx.lineTo(todayX, canvasHeight);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw application rows (starting from y=0)
    applications.forEach((app, index) => {
      const y = index * ROW_HEIGHT;
      const isCompleted = app.schedule_status === 'Completed';
      const isHovered = hoveredApp === app.id;

      // Row background
      if (isHovered) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.1)';
      } else if (index % 2 === 0) {
        ctx.fillStyle = '#ffffff';
      } else {
        ctx.fillStyle = '#f9fafb';
      }
      ctx.fillRect(0, y, canvasWidth, ROW_HEIGHT);

      // Row border
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y + ROW_HEIGHT);
      ctx.lineTo(canvasWidth, y + ROW_HEIGHT);
      ctx.stroke();

      // Draw phase bars
      const phaseBarHeight = 24;
      const phaseY = y + (ROW_HEIGHT - phaseBarHeight) / 2;

      app.phases.forEach((phase, phaseIndex) => {
        if (!phase.start_date || !phase.end_date) return;

        const phaseStart = parseDate(phase.start_date);
        const phaseEnd = parseDate(phase.end_date);
        if (!phaseStart || !phaseEnd) return;

        const startOffset = daysBetween(minDate, phaseStart);
        const endOffset = daysBetween(minDate, phaseEnd);
        const phaseX = startOffset * DAY_WIDTH;
        const phaseWidth = Math.max((endOffset - startOffset + 1) * DAY_WIDTH, 2);

        // Use grey colors for completed or deployed applications
        const useGrayscale = isCompleted || app.deployment_status === 'Deployed';
        const colors = useGrayscale ? GRAYSCALE_COLORS : (PHASE_COLORS[phase.name] || GRAYSCALE_COLORS);
        const isPhaseHovered = hoveredPhase?.appId === app.id && hoveredPhase?.phase === phase.name;

        // Phase bar gradient
        const gradient = ctx.createLinearGradient(phaseX, phaseY, phaseX, phaseY + phaseBarHeight);
        gradient.addColorStop(0, colors.primary);
        gradient.addColorStop(1, colors.secondary);

        // Draw bar with rounded corners
        ctx.fillStyle = gradient;
        ctx.beginPath();
        const radius = 4;
        ctx.roundRect(phaseX, phaseY, phaseWidth, phaseBarHeight, radius);
        ctx.fill();

        // Border
        ctx.strokeStyle = colors.border;
        ctx.lineWidth = isPhaseHovered ? 2 : 1;
        ctx.stroke();

        // Buffer indicator (lighter section at end)
        if (phase.buffer_days > 0 && phaseWidth > 20) {
          const bufferWidth = Math.min(phase.buffer_days * DAY_WIDTH, phaseWidth * 0.3);
          ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.fillRect(phaseX + phaseWidth - bufferWidth, phaseY, bufferWidth, phaseBarHeight);
        }

        // Phase label (if wide enough)
        if (phaseWidth > 100) {
          ctx.fillStyle = '#ffffff';
          ctx.font = '11px Inter, sans-serif';
          const shortName = phase.name.split(' ')[0];
          ctx.fillText(shortName, phaseX + 6, phaseY + 16);
        }
      });
    });
  }, [applications, zoomLevel, hoveredApp, hoveredPhase, getTimelineBounds, DAY_WIDTH]);

  // Redraw on state changes
  useEffect(() => {
    if (viewMode === 'gantt') {
      drawGanttChart();
    }
  }, [viewMode, drawGanttChart]);

  // Sync scroll between sidebar and timeline
  const handleTimelineScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (sidebarRef.current) {
      sidebarRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  }, []);

  const handleSidebarScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (timelineRef.current) {
      timelineRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  }, []);

  // Canvas mouse handling
  const handleCanvasMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Determine which row (no header offset since canvas only has rows)
    const rowIndex = Math.floor(y / ROW_HEIGHT);
    if (rowIndex >= 0 && rowIndex < applications.length) {
      const app = applications[rowIndex];
      setHoveredApp(app.id);

      // Determine which phase
      const { minDate } = getTimelineBounds();
      const dayOffset = x / DAY_WIDTH;
      const hoverDate = new Date(minDate.getTime() + dayOffset * 24 * 60 * 60 * 1000);

      let foundPhase: string | null = null;
      app.phases.forEach(phase => {
        const phaseStart = parseDate(phase.start_date);
        const phaseEnd = parseDate(phase.end_date);
        if (phaseStart && phaseEnd && hoverDate >= phaseStart && hoverDate <= phaseEnd) {
          foundPhase = phase.name;
        }
      });

      if (foundPhase) {
        setHoveredPhase({ appId: app.id, phase: foundPhase });
      } else {
        setHoveredPhase(null);
      }
    } else {
      setHoveredApp(null);
      setHoveredPhase(null);
    }
  }, [applications, getTimelineBounds, DAY_WIDTH]);

  const handleCanvasMouseLeave = useCallback(() => {
    setHoveredApp(null);
    setHoveredPhase(null);
  }, []);

  // Export to Excel
  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');

      const workbook = XLSX.utils.book_new();

      // Sheet 1: Summary
      const summaryData = applications.map(app => ({
        'Integration Group ID': app.integration_group_id,
        'Integration Group Name': app.integration_group_name,
        'Application ID': app.id,
        'Application Name': app.text,
        'Start Date': app.start_date || '',
        'End Date': app.end_date || '',
        'Total Duration (Days)': app.total_duration_days,
        'Total Effort (Days)': app.total_effort_days,
        'Status': app.schedule_status,
        'Recommendation': app.application_status
      }));

      // Sheet 2: Phase Details with all columns from spec
      const phaseData: any[] = [];
      applications.forEach(app => {
        const rowData: any = {
          'Integration Group ID': app.integration_group_id,
          'Integration Group Name': app.integration_group_name,
          'Application ID': app.id,
          'Application Name': app.text,
          'Overall Start': app.start_date || '',
          'Overall End': app.end_date || ''
        };

        // Add phase columns
        app.phases.forEach((phase, index) => {
          const phaseNum = index + 1;
          const shortName = phase.name.replace(/ /g, '_');
          rowData[`P${phaseNum}_Name`] = phase.name;
          rowData[`P${phaseNum}_Base_Days`] = phase.base_duration;
          rowData[`P${phaseNum}_Buffer_Days`] = phase.buffer_days;
          rowData[`P${phaseNum}_Total_Days`] = phase.total_duration;
          rowData[`P${phaseNum}_Start`] = phase.start_date || '';
          rowData[`P${phaseNum}_End`] = phase.end_date || '';
        });

        phaseData.push(rowData);
      });

      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      const phaseSheet = XLSX.utils.json_to_sheet(phaseData);

      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Roadmap Summary');
      XLSX.utils.book_append_sheet(workbook, phaseSheet, 'Phase Details');

      const fileName = `Implementation_Roadmap_${selectedGroupId}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export to Excel');
    }
  };

  // Zoom handlers
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.25));
  const handleZoomReset = () => setZoomLevel(1);

  // ============================================================================
  // Render Functions
  // ============================================================================

  // Draw timeline header on a separate canvas
  const drawTimelineHeader = useCallback((headerCanvas: HTMLCanvasElement | null) => {
    if (!headerCanvas || applications.length === 0) return;

    const ctx = headerCanvas.getContext('2d');
    if (!ctx) return;

    const { minDate, totalDays } = getTimelineBounds();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const canvasWidth = totalDays * DAY_WIDTH;

    // Set canvas size
    headerCanvas.width = canvasWidth;
    headerCanvas.height = HEADER_HEIGHT;

    // Clear and draw header background
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvasWidth, HEADER_HEIGHT);

    // Draw month/week headers
    let currentDate = new Date(minDate);
    let lastMonth = -1;

    while (currentDate <= new Date(minDate.getTime() + totalDays * 24 * 60 * 60 * 1000)) {
      const dayOffset = daysBetween(minDate, currentDate);
      const x = dayOffset * DAY_WIDTH;

      // Draw month labels
      if (currentDate.getMonth() !== lastMonth) {
        lastMonth = currentDate.getMonth();
        ctx.fillStyle = '#374151';
        ctx.font = 'bold 12px Inter, sans-serif';
        ctx.fillText(
          currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          x + 5,
          20
        );
      }

      // Draw day markers
      if (zoomLevel >= 0.75) {
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, HEADER_HEIGHT - 10);
        ctx.lineTo(x, HEADER_HEIGHT);
        ctx.stroke();

        // Day numbers for high zoom
        if (zoomLevel >= 1.5) {
          ctx.fillStyle = isBusinessDay(currentDate) ? '#6b7280' : '#d1d5db';
          ctx.font = '10px Inter, sans-serif';
          ctx.fillText(currentDate.getDate().toString(), x + 5, 45);
        }
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Draw TODAY marker in header
    const todayOffset = daysBetween(minDate, today);
    if (todayOffset >= 0 && todayOffset <= totalDays) {
      const todayX = todayOffset * DAY_WIDTH;

      // TODAY label
      ctx.fillStyle = '#EF4444';
      ctx.font = 'bold 10px Inter, sans-serif';
      const todayLabel = 'TODAY';
      const labelWidth = ctx.measureText(todayLabel).width + 10;
      ctx.fillRect(todayX - labelWidth / 2, 2, labelWidth, 16);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(todayLabel, todayX - labelWidth / 2 + 5, 14);
    }

    // Draw bottom border
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, HEADER_HEIGHT - 1);
    ctx.lineTo(canvasWidth, HEADER_HEIGHT - 1);
    ctx.stroke();
  }, [applications, zoomLevel, getTimelineBounds, DAY_WIDTH]);

  // Ref for header canvas
  const headerCanvasRef = useRef<HTMLCanvasElement>(null);

  // Draw header when needed
  useEffect(() => {
    if (viewMode === 'gantt') {
      drawTimelineHeader(headerCanvasRef.current);
    }
  }, [viewMode, drawTimelineHeader]);

  // Handle horizontal scroll sync for header
  const handleTimelineScrollWithHeader = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    // Sync sidebar vertical scroll
    if (sidebarRef.current) {
      sidebarRef.current.scrollTop = e.currentTarget.scrollTop;
    }
    // Sync header horizontal scroll
    const headerWrapper = document.querySelector('.roadmap-timeline-header-scroll') as HTMLElement;
    if (headerWrapper) {
      headerWrapper.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  const renderGanttView = () => {
    const { totalDays } = getTimelineBounds();
    const canvasWidth = totalDays * DAY_WIDTH;
    const canvasHeight = applications.length * ROW_HEIGHT;

    return (
      <div className="roadmap-gantt-container">
        {/* Sidebar with application names */}
        <div className="roadmap-sidebar" style={{ width: SIDEBAR_WIDTH }}>
          {/* Fixed header for sidebar */}
          <div className="roadmap-sidebar-header" style={{ height: HEADER_HEIGHT }}>
            <span>Application</span>
          </div>
          {/* Scrollable content - synced with timeline */}
          <div
            ref={sidebarRef}
            className="roadmap-sidebar-scroll"
            onScroll={handleSidebarScroll}
          >
            <div className="roadmap-sidebar-content">
              {applications.map((app, index) => (
                <div
                  key={app.id}
                  className={`roadmap-sidebar-row ${hoveredApp === app.id ? 'hovered' : ''}`}
                  style={{ height: `${ROW_HEIGHT}px`, minHeight: `${ROW_HEIGHT}px`, maxHeight: `${ROW_HEIGHT}px` }}
                  onMouseEnter={() => setHoveredApp(app.id)}
                  onMouseLeave={() => setHoveredApp(null)}
                >
                  <div className="app-info">
                    <div className="app-id">{app.id}</div>
                    <div className="app-name" title={app.text}>{app.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Timeline area */}
        <div className="roadmap-timeline-wrapper">
          {/* Fixed header area for timeline - scrolls horizontally only with content */}
          <div className="roadmap-timeline-header-scroll" style={{ height: HEADER_HEIGHT, overflow: 'hidden' }}>
            <canvas
              ref={headerCanvasRef}
              width={canvasWidth}
              height={HEADER_HEIGHT}
            />
          </div>
          {/* Scrollable timeline - synced with sidebar vertically and header horizontally */}
          <div
            ref={timelineRef}
            className="roadmap-timeline"
            onScroll={handleTimelineScrollWithHeader}
          >
            <canvas
              ref={canvasRef}
              width={canvasWidth}
              height={canvasHeight}
              onMouseMove={handleCanvasMouseMove}
              onMouseLeave={handleCanvasMouseLeave}
              style={{ cursor: 'crosshair' }}
            />
          </div>
        </div>

        {/* Hover tooltip */}
        {hoveredPhase && (
          <div className="phase-tooltip">
            {(() => {
              const app = applications.find(a => a.id === hoveredPhase.appId);
              const phase = app?.phases.find(p => p.name === hoveredPhase.phase);
              if (!app || !phase) return null;

              return (
                <>
                  <div className="tooltip-header">
                    <strong>{app.id}</strong> - {hoveredPhase.phase}
                  </div>
                  <div className="tooltip-body">
                    <div><strong>Start:</strong> {formatDate(parseDate(phase.start_date))}</div>
                    <div><strong>End:</strong> {formatDate(parseDate(phase.end_date))}</div>
                    <div><strong>Base Duration:</strong> {phase.base_duration} days</div>
                    <div><strong>Buffer:</strong> {phase.buffer_days} days</div>
                    <div><strong>Total:</strong> {phase.total_duration} days</div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  const renderTableView = () => {
    return (
      <div className="roadmap-table-container">
        <table className="roadmap-table">
          <thead>
            <tr>
              <th rowSpan={2} className="sticky-col col-grouping">Grouping</th>
              <th rowSpan={2} className="sticky-col col-application">Application</th>
              <th rowSpan={2} className="two-line-header">
                <div>Application</div>
                <div>Disposition</div>
                <div>Start Date</div>
              </th>
              <th rowSpan={2} className="two-line-header">
                <div>Application</div>
                <div>Disposition</div>
                <div>End Date</div>
              </th>
              <th rowSpan={2} className="two-line-header">
                <div>Duration</div>
                <div>(Calendar</div>
                <div>Days)</div>
              </th>
              <th rowSpan={2} className="two-line-header">
                <div>Duration</div>
                <div>(Business</div>
                <div>Days)</div>
              </th>
              {PHASE_ORDER.map((phase) => (
                <th
                  key={phase}
                  colSpan={3}
                  className="phase-header"
                  style={{
                    backgroundColor: PHASE_COLORS[phase]?.primary || '#6b7280',
                    color: 'white'
                  }}
                >
                  {phase}
                </th>
              ))}
            </tr>
            <tr>
              {PHASE_ORDER.map(phase => (
                <React.Fragment key={`${phase}-sub`}>
                  <th
                    className="sub-header"
                    style={{ backgroundColor: PHASE_COLORS[phase]?.bg || '#f3f4f6' }}
                  >
                    Duration
                  </th>
                  <th
                    className="sub-header"
                    style={{ backgroundColor: PHASE_COLORS[phase]?.bg || '#f3f4f6' }}
                  >
                    Start Date
                  </th>
                  <th
                    className="sub-header"
                    style={{ backgroundColor: PHASE_COLORS[phase]?.bg || '#f3f4f6' }}
                  >
                    End Date
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {applications.map(app => (
              <tr key={app.id} className={(app.schedule_status === 'Completed' || app.deployment_status === 'Deployed') ? 'completed-row' : ''}>
                <td className="sticky-col col-grouping stacked-cell">
                  <div className="stacked-id">{app.integration_group_id}</div>
                  {app.integration_group_name && (
                    <div className="stacked-name" title={app.integration_group_name}>{app.integration_group_name}</div>
                  )}
                </td>
                <td className="sticky-col col-application stacked-cell">
                  <div className="stacked-id">{app.id}</div>
                  {app.text && (
                    <div className="stacked-name" title={app.text}>{app.text}</div>
                  )}
                </td>
                <td className="date-cell">{formatDateMMDDYYYY(parseDate(app.start_date))}</td>
                <td className="date-cell">{formatDateMMDDYYYY(parseDate(app.end_date))}</td>
                <td className="number-cell">{app.total_duration_days}</td>
                <td className="number-cell">
                  {/* Sum of business days from all phases */}
                  {app.phases.reduce((sum, phase) => {
                    const phaseStart = parseDate(phase.start_date);
                    const phaseEnd = parseDate(phase.end_date);
                    if (phaseStart && phaseEnd) {
                      return sum + businessDaysBetween(phaseStart, phaseEnd);
                    }
                    return sum;
                  }, 0) || '-'}
                </td>
                {PHASE_ORDER.map(phaseName => {
                  const phase = app.phases.find(p => p.name === phaseName);
                  const bgColor = PHASE_COLORS[phaseName]?.bg || '#f9fafb';
                  const phaseStartDate = parseDate(phase?.start_date || null);
                  const phaseEndDate = parseDate(phase?.end_date || null);
                  // Calculate duration from actual dates
                  const phaseDuration = (phaseStartDate && phaseEndDate)
                    ? businessDaysBetween(phaseStartDate, phaseEndDate)
                    : '-';
                  return (
                    <React.Fragment key={`${app.id}-${phaseName}`}>
                      <td className="number-cell" style={{ backgroundColor: bgColor }}>
                        {phaseDuration}
                      </td>
                      <td className="date-cell" style={{ backgroundColor: bgColor }}>
                        {formatDateMMDDYYYY(phaseStartDate)}
                      </td>
                      <td className="date-cell" style={{ backgroundColor: bgColor }}>
                        {formatDateMMDDYYYY(phaseEndDate)}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <div className="page roadmap-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Roadmap Visualization</h1>
            <p className="page-header__subtitle">
              Visualize opportunities and roadmap schedules
            </p>
          </div>
          <div className="page-header__actions">
            {applications.length > 0 && (
              <button
                onClick={exportToExcel}
                className="btn btn-outline-success"
                title="Download current data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="roadmap-content">
        {/* Controls */}
        <div className="roadmap-controls">
          {/* View Toggle */}
          <div className="control-group">
            <div className="view-toggle">
              <button
                className={`toggle-btn ${viewMode === 'gantt' ? 'active' : ''}`}
                onClick={() => setViewMode('gantt')}
              >
                Gantt Chart
              </button>
              <button
                className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                Table
              </button>
            </div>
          </div>

          {/* Project Filter */}
          <div className="control-group">
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="group-select"
            >
              <option value="">Select Grouping</option>
              <option value="ALL">All Groupings</option>
              {integrationGroups.map(group => (
                <option key={group.group_id} value={group.group_id}>
                  {group.group_id} - {group.group_name}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date Picker (Gantt only) */}
          {viewMode === 'gantt' && (
            <div className="control-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={ganttStartDate}
                onChange={(e) => setGanttStartDate(e.target.value)}
                className="group-select"
                style={{ padding: '6px 10px', fontSize: '13px', minWidth: '150px' }}
              />
              {ganttStartDate && (
                <button
                  onClick={() => setGanttStartDate('')}
                  className="zoom-btn reset"
                  style={{ marginLeft: '4px' }}
                  title="Reset to auto-detect"
                >
                  Reset
                </button>
              )}
            </div>
          )}

          {/* Zoom Controls (Gantt only) */}
          {viewMode === 'gantt' && (
            <div className="control-group">
              <label>Zoom:</label>
              <div className="zoom-controls">
                <button onClick={handleZoomOut} className="zoom-btn" disabled={zoomLevel <= 0.25}>
                  -
                </button>
                <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                <button onClick={handleZoomIn} className="zoom-btn" disabled={zoomLevel >= 3}>
                  +
                </button>
                <button onClick={handleZoomReset} className="zoom-btn reset">
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Phase Legend */}
        <div className="phase-legend">
          <span className="legend-title">Phases:</span>
          {PHASE_ORDER.map(phase => (
            <div key={phase} className="legend-item">
              <div
                className="legend-color"
                style={{ backgroundColor: PHASE_COLORS[phase]?.primary || '#6b7280' }}
              />
              <span>{phase}</span>
            </div>
          ))}
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#9E9E9E' }} />
            <span>Deployed/Completed (Grayscale)</span>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="roadmap-loading">
            <div className="spinner"></div>
            <p style={{ fontSize: '16px' }}>Loading roadmap data...</p>
          </div>
        ) : error ? (
          <div className="roadmap-error">
            <p style={{ fontSize: '16px' }}>{error}</p>
            <button onClick={() => setSelectedGroupId(selectedGroupId)} className="btn btn-secondary">
              Retry
            </button>
          </div>
        ) : !selectedGroupId ? (
          <div className="roadmap-empty">
            <div className="empty-icon" style={{ fontSize: '16px' }}>Select a Group</div>
            <h3 style={{ fontSize: '16px' }}>Select Integration Group</h3>
            <p style={{ fontSize: '16px' }}>Select a specific integration group from the dropdown above to view the roadmap visualization.</p>
          </div>
        ) : applications.length === 0 ? (
          <div className="roadmap-empty">
            <div className="empty-icon" style={{ fontSize: '16px' }}>No Data</div>
            <h3 style={{ fontSize: '16px' }}>No Schedule Data</h3>
            <p style={{ fontSize: '16px' }}>No scheduled applications found for this group. Please ensure schedule data has been created in the Roadmap Schedule screen.</p>
          </div>
        ) : (
          <>
            {/* View Content */}
            {viewMode === 'gantt' ? renderGanttView() : renderTableView()}
          </>
        )}
      </div>
    </div>
  );
};

export default ImplementationRoadmap;
